import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Input, FormGroup, Label, Form, Row, Col, Card, CardBody, InputGroup, InputGroupText } from "reactstrap";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import Select from "react-select"; // Searchable dropdown

const EditSubClient = () => {
    const { id } = useParams(); // URL-la irundhu ID edukuroom machan
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [clients, setClients] = useState([]); // Parent Clients list
    const [formData, setFormData] = useState({
        id: id,
        client_id: null,
        name: "",
        email: "",
        phone: "",
        gstin: "",
        address: "",
        state_code: "",
        status: 1
    });

    const [errors, setErrors] = useState({});

    // 1. Initial Data Fetch (Parent Clients + Current Sub-Client Data)
    useEffect(() => {
        const fetchAllData = async () => {
            dispatch(loadingTrue());
            try {
                // Rendu API-yum parallel-ah hit panroom machan
                const [parentRes, subClientRes] = await Promise.all([
                    Handler({ method: "GET", url: "/clients/list.php" }),
                    Handler({ method: "GET", url: `/sub_client/view.php?id=${id}` })
                ]);

                // Set Parent Clients Dropdown
                let parentOptions = [];
                if (parentRes?.success) {
                    parentOptions = parentRes?.data?.map(c => ({
                        value: c?.id,
                        label: (c?.client?.name || c?.name)?.toUpperCase()
                    })) || [];
                    setClients(parentOptions);
                }

                // Set Sub-Client Current Data
                if (subClientRes?.success) {
                    const sc = subClientRes.data;
                    setFormData({
                        id: sc.id,
                        // Find and set the correct Parent Client object for React-Select
                        client_id: parentOptions.find(opt => opt.value === sc.parent_client.id) || null,
                        name: sc.sub_client.name || "",
                        email: sc.sub_client.email || "",
                        phone: sc.sub_client.phone || "",
                        gstin: sc.sub_client.gstin || "",
                        address: sc.sub_client.address || "",
                        state_code: sc.sub_client.state_code || "",
                        status: sc.status.value
                    });
                } else {
                    toast.error("Sub-client not found");
                    navigate("/sub-clients");
                }
            } catch (err) {
                toast.error("Error loading data");
            } finally {
                dispatch(loadingFalse());
            }
        };

        fetchAllData();
    }, [id]);

    // 2. Handle Input Changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        if (errors[name]) setErrors({ ...errors, [name]: "" });
    };

    const handleSelectChange = (selectedOption) => {
        setFormData({ ...formData, client_id: selectedOption });
    };

    // 3. Update Logic
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Basic Front-end validation
        if (!formData.client_id) return toast.error("Please select a parent client");
        if (!/^[0-9]{10}$/.test(formData.phone)) return toast.error("Invalid phone number");

        dispatch(loadingTrue());
        try {
            const res = await Handler({
                method: "POST", // Unga PHP-la $allowedMethods = ['POST'] nu irukku machan
                url: "/sub_client/update.php",
                data: {
                    ...formData,
                    client_id: formData.client_id?.value, // ID-vah mattum anupuroom
                    status: Number(formData.status)
                }
            });

            if (res?.success) {
                Swal.fire("Updated!", "Sub-client details updated successfully", "success");
                navigate("/sub-clients");
            } else {
                if (res?.errors) {
                    setErrors(res.errors);
                } else {
                    toast.error(res?.message || "Update failed");
                }
            }
        } catch (err) {
            Swal.fire("Error", "Server connection error", "error");
        } finally {
            dispatch(loadingFalse());
        }
    };

    return (
        <div className="page-container p-1">
            <div className="page-header mb-4">
                <h6 className="text-muted">Management &gt; Sub-Clients &gt; Edit</h6>
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <h3 className="fw-bold">Edit Sub-Client</h3>
                        <p className="text-muted small">Update profile or branch details for #{id}</p>
                    </div>
                    <Button color="secondary" outline onClick={() => navigate(-1)}>
                        <i className="bx bx-arrow-back"></i> Back
                    </Button>
                </div>
            </div>

            <Form onSubmit={handleSubmit}>
                <Row>
                    <Col lg={8}>
                        <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '15px' }}>
                            <CardBody className="p-4">
                                <h5 className="fw-bold mb-4">Basic Information</h5>
                                <Row>
                                    <Col md={12} className="mb-3">
                                        <FormGroup>
                                            <Label className="fw-bold">Parent Merchant <span className="text-danger">*</span></Label>
                                            <Select
                                                options={clients}
                                                value={formData.client_id}
                                                onChange={handleSelectChange}
                                                placeholder="Search Parent Client..."
                                                isSearchable
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={12} className="mb-3">
                                        <FormGroup>
                                            <Label className="fw-bold">Sub-Client Name <span className="text-danger">*</span></Label>
                                            <Input
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                className={errors.name ? "is-invalid" : ""}
                                                required
                                            />
                                            {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label className="fw-bold">Phone Number <span className="text-danger">*</span></Label>
                                            <InputGroup>
                                                <InputGroupText><i className="bx bx-phone"></i></InputGroupText>
                                                <Input
                                                    type="number"
                                                    name="phone"
                                                    value={formData.phone}
                                                    onChange={handleChange}
                                                    className={errors.phone ? "is-invalid" : ""}
                                                    required
                                                />
                                            </InputGroup>
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label className="fw-bold">Email Address</Label>
                                            <InputGroup>
                                                <InputGroupText><i className="bx bx-envelope"></i></InputGroupText>
                                                <Input
                                                    type="email"
                                                    name="email"
                                                    value={formData.email}
                                                    onChange={handleChange}
                                                />
                                            </InputGroup>
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <FormGroup className="mt-3">
                                    <Label className="fw-bold">Full Address</Label>
                                    <Input
                                        type="textarea"
                                        name="address"
                                        rows="3"
                                        value={formData.address}
                                        onChange={handleChange}
                                    />
                                </FormGroup>
                            </CardBody>
                        </Card>
                    </Col>

                    <Col lg={4}>
                        <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '15px' }}>
                            <CardBody className="p-4">
                                <h5 className="fw-bold mb-4">Status & Tax</h5>
                                <FormGroup>
                                    <Label className="fw-bold">Account Status</Label>
                                    <Input type="select" name="status" value={formData.status} onChange={handleChange}>
                                        <option value="1">Active</option>
                                        <option value="0">Inactive</option>
                                    </Input>
                                </FormGroup>
                                <FormGroup className="mt-3">
                                    <Label className="fw-bold">GSTIN</Label>
                                    <Input
                                        name="gstin"
                                        style={{ textTransform: 'uppercase' }}
                                        value={formData.gstin}
                                        onChange={handleChange}
                                    />
                                </FormGroup>
                                <FormGroup className="mt-3">
                                    <Label className="fw-bold">State Code</Label>
                                    <Input
                                        type="number"
                                        name="state_code"
                                        value={formData.state_code}
                                        onChange={handleChange}
                                    />
                                </FormGroup>
                            </CardBody>
                        </Card>

                        <Button type="submit" className="add-btn w-100 py-3 fw-bold shadow-sm" style={{ backgroundColor: '#f16436', border: 'none' }}>
                            <i className="bx bx-save me-1"></i> SAVE CHANGES
                        </Button>
                    </Col>
                </Row>
            </Form>
        </div>
    );
};

export default EditSubClient;